import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import vendedorService, { Vendedor } from '../services/vendedorService';

interface VendedorSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (vendedor: Vendedor) => void;
}

const VendedorSearch: React.FC<VendedorSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [vendedores, setVendedores] = useState<Vendedor[]>([]);
    const [vendedoresFiltrados, setVendedoresFiltrados] = useState<Vendedor[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadVendedores = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const items = await vendedorService.getVendedores();
            setVendedores(items);
            setVendedoresFiltrados(items);
            if (items.length === 0) {
                setError('No se encontraron vendedores.');
            }
        } catch (err: any) {
            setError(`Error al cargar vendedores: ${err.message || 'Error desconocido'}`);
            setVendedores([]);
            setVendedoresFiltrados([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSearch = useCallback(() => {
        const trimmed = searchTerm.trim();
        if (!trimmed) {
            setVendedoresFiltrados(vendedores);
            setError(null);
            return;
        }

        const trimmedLower = trimmed.toLowerCase();
        // Buscar por número de vendedor o nombre
        const filtered = vendedores.filter(v => {
            const nroVendedor = String(v.nroVendedor).includes(trimmed);
            const nombre = v.nombreVendedor?.toLowerCase().includes(trimmedLower);
            return nroVendedor || nombre;
        });

        setVendedoresFiltrados(filtered);
        if (filtered.length === 0) {
            setError('No se encontraron vendedores.');
        } else {
            setError(null);
        }
    }, [searchTerm, vendedores]);

    const handleSelect = (vendedor: Vendedor) => {
        onSelect(vendedor);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setVendedoresFiltrados(vendedores);
        setError(null);
        onClose();
    };

    // Cargar vendedores cuando se abre el diálogo
    useEffect(() => {
        if (open) {
            loadVendedores();
        } else {
            setSearchTerm('');
            setVendedores([]);
            setVendedoresFiltrados([]);
            setError(null);
        }
    }, [open, loadVendedores]);

    // Filtrar cuando cambia el término de búsqueda
    useEffect(() => {
        if (open && vendedores.length > 0) {
            handleSearch();
        }
    }, [open, searchTerm, vendedores, handleSearch]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Vendedor
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Número de vendedor o nombre"
                        inputProps={{ 'aria-label': 'Buscar vendedor' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && vendedoresFiltrados.length > 0 && (
                    <>
                        {isSmallScreen ? (
                            // Vista de tarjetas para móviles
                            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                                <Stack spacing={2}>
                                    {vendedoresFiltrados.map((vendedor) => (
                                        <Card 
                                            key={vendedor.nroVendedor} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    boxShadow: 3
                                                }
                                            }}
                                            onClick={() => handleSelect(vendedor)}
                                        >
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                        Nro: {vendedor.nroVendedor}
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(vendedor);
                                                        }}
                                                        sx={{ minWidth: 'auto', px: 2 }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </Box>
                                                <Divider sx={{ my: 1.5 }} />
                                                <Stack spacing={1}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Nombre
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {vendedor.nombreVendedor?.trim() || '-'}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </Box>
                        ) : (
                            // Vista de tabla para pantallas grandes
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Nro. Vendedor</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Nombre</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {vendedoresFiltrados.map((vendedor) => (
                                            <TableRow 
                                                key={vendedor.nroVendedor} 
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => handleSelect(vendedor)}
                                            >
                                                <TableCell>{vendedor.nroVendedor}</TableCell>
                                                <TableCell>{vendedor.nombreVendedor?.trim() || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(vendedor);
                                                        }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}

                {!loading && vendedoresFiltrados.length === 0 && !error && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron vendedores
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button 
                    onClick={handleClose} 
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default VendedorSearch;

